/**
 * Heading primitive — design.md § Typography.
 *
 * Levels: h1, h2, h3 mapping to design.md typography (h1/h2/h3 size + weight + tracking).
 * Renders appropriate HTML element (h1/h2/h3).
 * Uses font-sans (Space Grotesk).
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type HeadingLevel = 'h1' | 'h2' | 'h3';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level: HeadingLevel;
  children: ReactNode;
}

const levelClasses: Record<HeadingLevel, string> = {
  h1: 'text-h1 font-medium tracking-tight',
  h2: 'text-h2 font-medium tracking-tight',
  h3: 'text-h3 font-medium tracking-tight',
};

const TagMap: Record<HeadingLevel, 'h1' | 'h2' | 'h3'> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
};

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  function Heading(
    {
      level,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const Tag = TagMap[level];
    return (
      <Tag
        ref={ref}
        className={cn(
          'font-sans',
          levelClasses[level],
          className,
        )}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);
